import { FormEvent, useEffect, useRef, useState } from "react";
import { Lock, LogIn, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchStoreCsrf, getCurrentStoreUser, loginStore } from "./api";
import AdminPasswordInput from "../admin/components/AdminPasswordInput";

export default function StoreLogin() {
  const navigate = useNavigate();
  const usernameInputRef = useRef<HTMLInputElement | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStoreCsrf()
      .then(() => getCurrentStoreUser())
      .then(() => navigate("/store/dashboard", { replace: true }))
      .catch(() => undefined);
  }, [navigate]);

  useEffect(() => {
    usernameInputRef.current?.focus();
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");

    if (!username.trim() || !password) {
      setMessage("請輸入帳號與密碼。");
      return;
    }

    setSubmitting(true);
    try {
      await loginStore(username.trim(), password);
      navigate("/store/dashboard", { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "登入失敗，請確認帳號密碼。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#f5f7f4] text-[#24352d]">
      <div className="mx-auto flex min-h-screen w-full max-w-[1180px] items-center justify-center px-5 py-10">
        <section className="grid w-full max-w-[980px] overflow-hidden rounded-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.12)] md:grid-cols-[.95fr_1.05fr]">
          <div className="bg-[#203d32] p-8 text-white md:p-10">
            <div className="mb-10">
              <div className="text-xl font-extrabold tracking-[.08em]">
                富聆聽<span className="text-[#d4a574]">健康</span>
              </div>
              <div className="mt-1 text-xs font-bold tracking-[.22em] text-[#d8c8b1]">FULING HEARING HEALTH</div>
            </div>

            {/* <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-md bg-white/10 text-[#d8c8b1]">
              <Store size={24} />
            </div> */}
            <h1 className="mb-4 text-3xl font-bold leading-tight md:text-4xl">門市端工作台</h1>
            <p className="text-base leading-8 text-[#d9e4de]">
              供各門市同仁處理今日預約、客戶聯絡、到店服務、回訪待辦與門市取貨。
            </p>
          </div>

          <form className="grid gap-5 p-8 md:p-10" onSubmit={handleSubmit}>
            <div>
              <p className="mb-2 text-sm font-bold tracking-[.16em] text-[#315746]">STORE LOGIN</p>
              <h2 className="text-2xl font-bold text-[#203d32]">門市同仁登入</h2>
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-username">
                帳號
              </label>
              <input
                id="store-username"
                ref={usernameInputRef}
                autoComplete="username"
                className="h-12 w-full rounded-md border border-[#cfd8d2] px-4 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                maxLength={80}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-password">
                密碼
              </label>
              <AdminPasswordInput
                id="store-password"
                autoComplete="current-password"
                className="h-12 w-full rounded-md border border-[#cfd8d2] px-4 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                value={password}
                onChange={setPassword}
              />
            </div>

            {message && (
              <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">
                {message}
              </div>
            )}

            <button
              className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={submitting}
              type="submit"
            >
              {submitting ? <Lock size={18} /> : <LogIn size={18} />}
              {submitting ? "登入中" : "登入門市端"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
